import { Injectable, inject } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Observable } from 'rxjs'
import { map, delay } from 'rxjs/operators'
import { IProposalData, IDocumentOption, IEmailTemplate } from '../models/email-proposal.models'

export interface IEmailProposalMockData {
    proposalData: IProposalData
    documents: IDocumentOption[]
    emailTemplates: IEmailTemplate[]
}

@Injectable({
    providedIn: 'root',
})
export class EmailProposalMockService {
    private http = inject(HttpClient)

    private readonly mockDataUrl = '/assets/mock/email-proposal.json'

    /**
     * Loads proposal data from mock JSON file
     */
    loadProposalData(proposalId: string): Observable<IProposalData> {
        return this.http.get<IEmailProposalMockData>(this.mockDataUrl).pipe(
            map((mockData) => ({
                ...mockData.proposalData,
                id: proposalId, // Override with the requested ID
            })),
            delay(500), // Simulate network delay
        )
    }

    /**
     * Loads document options from mock JSON file
     */
    loadDocuments(proposalId: string): Observable<IDocumentOption[]> {
        return this.http.get<IEmailProposalMockData>(this.mockDataUrl).pipe(
            map((mockData) => mockData.documents),
            delay(300), // Simulate network delay
        )
    }

    /**
     * Loads email templates from mock JSON file
     */
    loadEmailTemplates(): Observable<IEmailTemplate[]> {
        return this.http.get<IEmailProposalMockData>(this.mockDataUrl).pipe(
            map((mockData) => mockData.emailTemplates),
            delay(200), // Simulate network delay
        )
    }

    /**
     * Loads all mock data at once
     */
    loadAllMockData(): Observable<IEmailProposalMockData> {
        return this.http.get<IEmailProposalMockData>(this.mockDataUrl).pipe(
            delay(400), // Simulate network delay
        )
    }

    /**
     * Simulates sending an email (for development/testing)
     */
    sendEmail(): Observable<boolean> {
        return new Observable((observer) => {
            setTimeout(() => {
                // Simulate success/failure randomly for testing
                const success = Math.random() > 0.1 // 90% success rate
                if (success) {
                    observer.next(true)
                    observer.complete()
                } else {
                    observer.error(new Error('Failed to send email - network error'))
                }
            }, 2000) // Simulate 2-second delay
        })
    }
}
